/**
 * HubCreating — the new-library creation form.
 *
 * Three controlled fields:
 *   1. Library name (required, free text).
 *   2. Output target — single-select chip set: CSR / CMC / IB /
 *      Protocol / General. Maps to ``Library.scope``.
 *   3. Bind to draft (optional) — placeholder for the doctype YAML
 *      auto-binding the spec's M1b will cover. For now, render a
 *      read-only "current draft" hint; full picker arrives with M6.
 *
 * On Create: parent receives ``onCreate({ name, scope })`` and is
 *   responsible for the actual ``createLibrary`` API call. Form
 *   stays mounted in the meantime so an in-flight create error can
 *   surface inline (handled by the parent's submission flow).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";

/**
 * Library scope vocabulary — must match the ``Library.scope``
 * union in addin/src/types/index.ts. The order here is the
 * default UI order (most common first).
 */
export type LibraryScope = "CSR" | "CMC" | "IB" | "Protocol" | "General";

const SCOPES: ReadonlyArray<{ id: LibraryScope; label: string }> = [
  { id: "CSR", label: "CSR" },
  { id: "CMC", label: "CMC" },
  { id: "IB", label: "IB" },
  { id: "Protocol", label: "Protocol" },
  { id: "General", label: "General" },
] as const;

export interface HubCreatingProps {
  onCreate: (input: {
    name: string;
    scope: LibraryScope;
  }) => void;
  onCancel: () => void;
  /** Optional initial values (used by tests + by M5/M6 flows that
   *  may pre-fill the form from a doctype context). */
  initialName?: string;
  initialScope?: LibraryScope;
  /** Disabled state for the submit button while a create call is
   *  in flight. Parent owns the in-flight state. */
  submitting?: boolean;
  /** Inline error surfaced when the create call fails — keeps the
   *  writer on the form instead of navigating to a phantom library. */
  error?: string | null;
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    padding: "14px",
    backgroundColor: surfaceColors.paper,
    fontFamily: fonts.body,
    overflow: "auto",
  },
  title: {
    fontSize: "12px",
    fontWeight: 700,
    fontFamily: fonts.display,
    color: inkColors.ink,
    marginBottom: "4px",
  },
  blurb: {
    fontSize: "10.5px",
    color: inkColors.ink3,
    marginBottom: "14px",
  },
  fieldGroup: {
    marginBottom: "12px",
  },
  fieldGroupLast: {
    marginBottom: "14px",
  },
  fieldLabel: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: "4px",
    display: "block",
  },
  textInput: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    padding: "8px 10px",
    backgroundColor: "var(--surface-raised)",
    fontSize: "12px",
    color: inkColors.ink,
    fontFamily: fonts.body,
    ":focus": {
      border: `1px solid ${brandColors.primary}`,
      outline: "none",
      boxShadow: `0 0 0 3px ${brandColors.light}`,
    },
  },
  scopeRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "5px",
    rowGap: "5px",
  },
  scopeChip: {
    fontSize: "10px",
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: "14px",
    backgroundColor: "transparent",
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.line}`,
    cursor: "pointer",
    fontFamily: fonts.body,
    ":hover": {
      border: `1px solid ${brandColors.primary}`,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  scopeChipActive: {
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    border: `1px solid ${brandColors.primary}`,
    ":hover": {
      backgroundColor: brandColors.primary,
    },
  },
  bindHint: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    padding: "8px 10px",
    backgroundColor: surfaceColors.paperDim,
    fontSize: "11px",
    color: inkColors.ink3,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  actionRow: {
    display: "flex",
    columnGap: "6px",
  },
  submit: {
    flex: 1,
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    border: "none",
    borderRadius: "4px",
    padding: "8px",
    fontSize: "11px",
    fontWeight: 700,
    fontFamily: fonts.body,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    cursor: "pointer",
    ":hover": { opacity: 0.9 },
    ":disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  cancel: {
    backgroundColor: "transparent",
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    padding: "8px 14px",
    fontSize: "11px",
    fontWeight: 600,
    fontFamily: fonts.body,
    cursor: "pointer",
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
    },
  },
  error: {
    marginBottom: "8px",
    padding: "8px 10px",
    borderRadius: "4px",
    backgroundColor: "var(--danger-soft)",
    border: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
    color: "var(--danger)",
    fontSize: "10.5px",
    fontFamily: fonts.body,
    lineHeight: 1.4,
  },
});

export const HubCreating: React.FC<HubCreatingProps> = ({
  onCreate,
  onCancel,
  initialName = "",
  initialScope = "CSR",
  submitting = false,
  error = null,
}) => {
  const styles = useStyles();
  const [name, setName] = useState(initialName);
  const [scope, setScope] = useState<LibraryScope>(initialScope);

  const canSubmit = name.trim().length > 0 && !submitting;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSubmit) return;
    onCreate({ name: name.trim(), scope });
  };

  return (
    <form onSubmit={handleSubmit} className={styles.root} data-testid="hub-creating">
      <div className={styles.blurb}>Group sources by output target. You can add or move files later.</div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="hub-creating-name">
          Library name
        </label>
        <input
          id="hub-creating-name"
          className={styles.textInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="KEYNOTE-189 CSR sources"
          aria-required="true"
        />
      </div>

      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>Output target</span>
        <div className={styles.scopeRow} role="radiogroup" aria-label="Output target">
          {SCOPES.map((s) => {
            const active = s.id === scope;
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={active}
                onClick={() => setScope(s.id)}
                className={mergeClasses(styles.scopeChip, active && styles.scopeChipActive)}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.fieldGroupLast}>
        <span className={styles.fieldLabel}>Bind to draft (optional)</span>
        <div className={styles.bindHint}>
          <span>Bind on the next draft start.</span>
          <span
            style={{
              fontSize: "9px",
              color: inkColors.ink4,
              fontFamily: fonts.mono,
            }}
          >
            auto
          </span>
        </div>
      </div>

      {error && (
        <div className={styles.error} role="alert" data-testid="hub-creating-error">
          {error}
        </div>
      )}

      <div className={styles.actionRow}>
        <button type="submit" disabled={!canSubmit} className={styles.submit}>
          {submitting ? "Creating…" : "Create & add files"}
        </button>
        <button type="button" onClick={onCancel} className={styles.cancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default HubCreating;
